import { AttachmentFile, DocumentFile, MediaFile } from '@nextforce/api'
import { Image, makeStyles, Text, useTheme } from '@rneui/themed'
import { font } from 'app/theme'
import { useMembers } from 'chat/members/queries/list'
import { blobToBase64 } from 'chat/shared/util'
import { isImageType, isPdfType } from 'file_folder_repository/util'
import { useCallback, useMemo, useRef } from 'react'
import {
  Mention,
  MentionsInput,
  MentionsInputStyle,
  SuggestionDataItem,
} from 'react-mentions'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { ComposerProps } from 'react-native-gifted-chat'
import { useUser } from 'users/queries/detail'
import { UserAvatar } from '../user/user_avatar'

const TEXT_INPUT_TOP_PADDING = 10

interface CustomTextInputProps {
  props: ComposerProps
  chatGroupId: string
  onTextChanged(text: string): void
  pasteAttachments: (chatGroupId: string, files: AttachmentFile[]) => void
  text: string
  disabled: boolean
  placeholder?: string
}

interface CustomSuggestionDataItem extends SuggestionDataItem {
  photoUrl: string | undefined
}

export default function CustomTextInput({
  props,
  chatGroupId,
  onTextChanged,
  pasteAttachments,
  text,
  disabled,
  placeholder,
}: CustomTextInputProps) {
  const styles = useStyles()
  const textInputRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()
  const pasteAttachmentsInChat = useCallback(
    (files: AttachmentFile[]) => {
      pasteAttachments(chatGroupId, files)
    },
    [chatGroupId, pasteAttachments]
  )

  // Fetch members for mention suggestions
  const userQuery = useUser()
  const membersQuery = useMembers(chatGroupId)
  const memberSuggestions = useMemo(() => {
    if (!membersQuery.data) {
      return []
    }
    return membersQuery.data
      .filter(member => member.id !== userQuery.data?.id)
      .map(member => ({
        id: member.id,
        display: `${member.firstName || ''} ${member.lastName || ''}`.trim(),
        photoUrl: member.photoUrl,
      }))
  }, [membersQuery.data, userQuery.data])

  // Function to render member suggestions
  const renderSuggestion = useCallback(
    (
      suggestion: CustomSuggestionDataItem,
      search: string,
      highlightedDisplay: React.ReactNode
    ) => {
      return (
        <TouchableOpacity
          key={suggestion.id}
          style={styles.mentionSuggestionContainer}
        >
          <UserAvatar
            user={{
              id: suggestion.id.toString(),
              photoUrl: suggestion.photoUrl,
            }}
            showAvatarIfNoPhone
            size={'small'}
          />
          <Text style={styles.mentionSuggestionText}>{highlightedDisplay}</Text>
        </TouchableOpacity>
      )
    },
    [styles.mentionSuggestionContainer, styles.mentionSuggestionText]
  )

  const mentionsInputStyle: MentionsInputStyle = {
    control: {
      fontSize: 14,
      fontFamily: font.normal,
      minHeight: 45,
    },
    highlighter: {
      padding: 9,
      border: '1px solid transparent',
      maxHeight: 100,
    },
    input: {
      padding: 9,
      color: disabled ? 'grey' : theme.colors.black,
      border: 'none',
      outline: 'none',
      overflow: 'auto',
      maxHeight: 100,
    },
  }

  return (
    <div
      ref={textInputRef}
      onDrop={e => {
        e.preventDefault()
        const items: any = [].slice
          .call(e.dataTransfer?.items)
          .filter((item: DataTransferItem) => {
            return item.kind === 'file'
          })
        if (items.length === 0) {
          return
        }
        const files: File[] = items.map((item: DataTransferItem) =>
          item.getAsFile()
        )
        return handleAttachmentPaste(files, pasteAttachmentsInChat)
      }}
      onPaste={async e => {
        const clipboardItems = e.clipboardData.items

        const fileItems: DataTransferItem[] = [].slice
          .call(clipboardItems)
          .filter((item: DataTransferItem) => {
            return item.kind === 'file'
          })
        if (fileItems.length > 0) {
          e.preventDefault()
          const files: File[] = fileItems
            .map((item: DataTransferItem) => item.getAsFile())
            .filter((file: File | null): file is File => file !== null)
          return handleAttachmentPaste(files, pasteAttachmentsInChat)
        }
      }}
      style={styles.textInputContainer}
    >
      <MentionsInput
        value={text}
        onChange={e => onTextChanged(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={mentionsInputStyle}
        forceSuggestionsAboveCursor
      >
        <Mention
          trigger="@"
          data={memberSuggestions}
          renderSuggestion={renderSuggestion}
          displayTransform={(id, display) => `@${display}`}
          markup="@[__display__](__id__)"
          appendSpaceOnAdd={true}
          style={styles.mentionText}
        />
      </MentionsInput>
    </div>
  )
}

export const handleAttachmentPaste = async (
  files: File[],
  pasteAttachmentsInChat: (files: AttachmentFile[]) => void
) => {
  files.forEach(async file => {
    const result = await blobToBase64(file)
    const fileUrl: string = result as string
    const fileName = file.name
    const fileType = file.type

    if (fileUrl) {
      if (isImageType(fileType)) {
        Image.getSize(fileUrl, (imgWidth, imgHeight) => {
          const mediaFile: MediaFile = {
            uri: fileUrl,
            height: imgHeight,
            width: imgWidth,
            type: 'media',
            mimeType: fileType,
          }
          pasteAttachmentsInChat([mediaFile])
        })
      } else if (fileType.startsWith('video')) {
        const video = document.createElement('video')
        video.crossOrigin = 'anonymous'

        const cleanupVideoListeners = () => {
          video.removeEventListener('loadedmetadata', handleVideoLoad)
          video.removeEventListener('error', handleVideoError)
        }

        const handleVideoLoad = function () {
          const videoWidth = video.videoWidth || 640
          const videoHeight = video.videoHeight || 480

          const mediaFile: MediaFile = {
            uri: fileUrl,
            height: videoHeight,
            width: videoWidth,
            type: 'media',
            mimeType: fileType,
          }
          pasteAttachmentsInChat([mediaFile])

          cleanupVideoListeners()
        }

        const handleVideoError = () => {
          console.error('Failed to load video metadata')

          if (
            fileType === 'video/quicktime' ||
            fileUrl.toLowerCase().endsWith('.mov')
          ) {
            const mediaFile: MediaFile = {
              uri: fileUrl,
              height: 480,
              width: 640,
              type: 'media',
              mimeType: fileType,
            }
            pasteAttachmentsInChat([mediaFile])
          }

          cleanupVideoListeners()
        }

        video.addEventListener('loadedmetadata', handleVideoLoad)
        video.onerror = handleVideoError

        video.src = fileUrl
      } else if (isPdfType(fileType)) {
        const documentFile: DocumentFile = {
          name: fileName,
          uri: fileUrl,
          type: 'document',
          mimeType: fileType,
        }
        pasteAttachmentsInChat([documentFile])
      }
    }
  })
}

const useStyles = makeStyles(theme => ({
  text: {
    flex: 1,
    padding: 6,
    fontFamily: 'sans-serif',
    borderStyle: 'none',
    outlineStyle: 'none',
    resize: 'none',
    overflow: 'hidden',
    color: theme.colors.black,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.grey6,
  },
  mentionText: {
    backgroundColor: theme.mode === 'dark' ? '#1E8555' : '#27BE64',
  },
  textInputContainer: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  mentionSuggestionText: {
    color: theme.colors.black,
    marginLeft: 12,
  },
  mentionSuggestionContainer: {
    width: 250,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey7 : theme.colors.white,
  },
}))
